<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight, Check, PencilLine } from 'lucide-vue-next';
import type { Component } from 'vue';
import { computed } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import {
    bulkImageNavItems,
    editorImageNavItem,
    optimizeImageNavItems,
    transformImageNavItems,
} from '@/data/imageHeaderNav';
import { localizeHref } from '@/lib/localizeHref';
import { dashboard } from '@/routes';
import {
    bulkResize,
    compress,
    editor,
    removeBackground,
    resize,
} from '@/routes/tools/image';

const { t, tList, locale } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.image.home.seo',
    fallbackTitleKey: 'tools.nav.home',
    fallbackTitleLabel: 'Home',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: dashboard.url(),
    includeBreadcrumbs: false,
});

const editorHref = computed(() => localizeHref(editor.url(), locale.value));

const heroFeatures = computed(() =>
    tList('tools.image.home.hero.features', [
        'Crop, filter, annotate, and watermark in one touch-friendly editor',
        'Undo, redo, and full-screen mode—processing stays in your browser',
        'Plus resize, compress, and convert when you need quick server-side tools',
    ]),
);

const editorSpotlightFeatures = computed(() =>
    tList('tools.image.home.editor_spotlight.features', [
        'Adjust, Finetune, Filters, Watermark, Annotate, and Resize tabs',
        'Save downloads your edited file—nothing is uploaded for editing',
        'Works on desktop and mobile with the same toolbar',
    ]),
);

const heroCtas = computed(() => [
    {
        href: editorHref.value,
        label: t('tools.image.home.hero.cta_editor', 'Edit image'),
        variant: 'default' as const,
        primary: true,
    },
    {
        href: localizeHref(resize.url(), locale.value),
        label: t('tools.image.home.hero.cta_resize', 'Resize image'),
        variant: 'outline' as const,
        primary: false,
    },
    {
        href: localizeHref(compress.url(), locale.value),
        label: t('tools.image.home.hero.cta_compress', 'Compress image'),
        variant: 'outline' as const,
        primary: false,
    },
    {
        href: localizeHref(removeBackground.url(), locale.value),
        label: t(
            'tools.image.home.hero.cta_remove_background',
            'Remove background',
        ),
        variant: 'outline' as const,
        primary: false,
    },
    {
        href: localizeHref(bulkResize.url(), locale.value),
        label: t('tools.image.home.hero.cta_bulk', 'Bulk tools'),
        variant: 'outline' as const,
        primary: false,
    },
]);

type ToolCard = {
    key: string;
    href: string;
    title: string;
    description: string;
    icon: Component;
    iconClass: string;
};

function toToolCard(
    item: typeof editorImageNavItem & { href: string },
): ToolCard {
    return {
        key: item.key,
        href: item.href,
        title: item.title,
        description: item.description,
        icon: item.icon,
        iconClass: item.iconClass,
    };
}

function localizeToolCard(item: {
    key: string;
    title: string;
    href: string;
    description: string;
    icon: Component;
    iconClass: string;
}): ToolCard {
    return {
        ...toToolCard(item),
        href: localizeHref(item.href, locale.value),
    };
}

const sections = computed(() => {
    const blocks: {
        headingKey: string;
        headingFallback: string;
        tools: ToolCard[];
        featured?: boolean;
    }[] = [
        {
            headingKey: 'tools.image.home.section_editor',
            headingFallback: 'Image editor',
            tools: [localizeToolCard(editorImageNavItem)],
            featured: true,
        },
        {
            headingKey: 'tools.image.home.section_transform',
            headingFallback: 'Transform',
            tools: transformImageNavItems.map(localizeToolCard),
        },
        {
            headingKey: 'tools.image.home.section_optimize',
            headingFallback: 'Optimize',
            tools: optimizeImageNavItems.map(localizeToolCard),
        },
        {
            headingKey: 'tools.image.home.section_bulk',
            headingFallback: 'Bulk',
            tools: bulkImageNavItems.map(localizeToolCard),
        },
    ];

    return blocks;
});
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-image-home" />

    <div
        class="flex h-full flex-1 flex-col gap-8 overflow-x-auto rounded-xl p-3 sm:gap-10 sm:p-4 md:p-6"
        data-testid="image-site-home"
    >
        <section
            class="relative overflow-hidden rounded-2xl border border-blue-200/70 bg-gradient-to-br from-blue-50 via-sky-50/90 to-emerald-50/80 shadow-sm dark:border-blue-900/50 dark:from-blue-950/80 dark:via-sky-950/60 dark:to-slate-950/80"
        >
            <div
                class="pointer-events-none absolute -top-24 -right-24 size-64 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-500/10"
                aria-hidden="true"
            />
            <div
                class="pointer-events-none absolute -bottom-20 -left-16 size-56 rounded-full bg-emerald-400/15 blur-3xl dark:bg-emerald-500/10"
                aria-hidden="true"
            />

            <div
                class="relative grid gap-6 p-4 sm:gap-8 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)] lg:items-center lg:gap-10 lg:p-8"
            >
                <div
                    class="order-2 w-full space-y-4 sm:space-y-5 lg:order-1 lg:max-w-2xl"
                >
                    <p
                        class="text-center text-xs font-semibold tracking-wide text-blue-800 uppercase sm:text-left dark:text-blue-300"
                    >
                        {{
                            t(
                                'tools.image.home.hero.eyebrow',
                                'Free browser image editor',
                            )
                        }}
                    </p>
                    <div class="space-y-3 text-center sm:text-left">
                        <h1
                            class="text-2xl font-bold tracking-tight text-balance text-foreground sm:text-3xl lg:text-4xl"
                        >
                            {{
                                t(
                                    'tools.image.home.hero.title',
                                    'Edit images in your browser—crop, filter, and annotate',
                                )
                            }}
                        </h1>
                        <p
                            class="text-sm leading-relaxed text-pretty text-muted-foreground sm:text-base"
                        >
                            {{
                                t(
                                    'tools.image.home.hero.lead',
                                    'Open the editor, upload a photo, and download when you are done. No install—resize and compress are here too when you need them.',
                                )
                            }}
                        </p>
                    </div>
                    <ul class="space-y-2.5" role="list">
                        <li
                            v-for="(feature, index) in heroFeatures"
                            :key="index"
                            class="flex gap-2.5 text-sm text-muted-foreground"
                        >
                            <Check
                                class="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400"
                                aria-hidden="true"
                            />
                            <span class="text-pretty">{{ feature }}</span>
                        </li>
                    </ul>
                    <div
                        class="flex flex-wrap justify-center gap-2.5 pt-1 sm:justify-start"
                    >
                        <Button
                            v-for="cta in heroCtas"
                            :key="cta.href"
                            :variant="cta.variant"
                            :class="
                                cta.primary
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500'
                                    : 'border-blue-200/80 bg-white/60 hover:bg-white/90 dark:border-blue-800/60 dark:bg-blue-950/40 dark:hover:bg-blue-950/70'
                            "
                            as-child
                        >
                            <Link :href="cta.href" prefetch>
                                {{ cta.label }}
                                <ArrowRight
                                    v-if="cta.primary"
                                    class="size-4"
                                    aria-hidden="true"
                                />
                            </Link>
                        </Button>
                    </div>
                </div>

                <div class="order-1 w-full lg:order-2 lg:justify-self-end">
                    <div
                        class="flex w-full flex-col gap-3 rounded-2xl border border-white/60 bg-white/55 p-4 shadow-sm backdrop-blur-sm sm:gap-4 sm:p-6 lg:max-w-[20rem] lg:p-8 dark:border-white/10 dark:bg-white/5"
                    >
                        <div
                            class="flex items-center gap-4 sm:flex-col sm:items-center sm:gap-4"
                        >
                            <img
                                src="/images/image/android-chrome-192x192.png"
                                alt=""
                                width="120"
                                height="120"
                                class="size-16 shrink-0 object-contain sm:size-[7.5rem]"
                                loading="eager"
                                decoding="async"
                            />
                            <div
                                class="min-w-0 flex-1 space-y-1.5 text-left sm:w-full sm:text-center"
                            >
                                <p
                                    class="inline-flex min-w-0 items-baseline gap-[2px] text-base font-bold tracking-tight select-none sm:justify-center sm:text-lg"
                                >
                                    <span
                                        class="text-neutral-950 dark:text-neutral-50"
                                        >optimize</span
                                    >
                                    <span
                                        class="text-emerald-600 dark:text-emerald-400"
                                        >image</span
                                    >
                                    <sup
                                        class="[vertical-align:0.35em] align-baseline text-[0.42em] leading-none font-semibold text-neutral-600 dark:text-neutral-400"
                                        >.net</sup
                                    >
                                </p>
                                <p
                                    class="text-sm leading-relaxed font-medium text-pretty text-foreground"
                                >
                                    {{
                                        t(
                                            'tools.image.home.hero.brand_tagline',
                                            'Touch-friendly editing plus resize, compress, and convert—all in the browser.',
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                        <p
                            class="text-xs leading-relaxed text-pretty text-muted-foreground sm:text-center"
                        >
                            {{
                                t(
                                    'tools.image.home.hero.brand_note',
                                    'The editor runs locally in your browser; other tools process uploads on our servers only for the job.',
                                )
                            }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section
            class="rounded-2xl border border-blue-200/60 bg-gradient-to-r from-blue-50/80 to-transparent p-5 shadow-sm sm:p-6 dark:border-blue-900/40 dark:from-blue-950/40"
            aria-labelledby="editor-spotlight-heading"
        >
            <div
                class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between"
            >
                <div class="flex min-w-0 flex-1 gap-4">
                    <div
                        class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm dark:bg-blue-600"
                    >
                        <PencilLine class="size-6" aria-hidden="true" />
                    </div>
                    <div class="min-w-0 space-y-2">
                        <p
                            class="text-xs font-semibold tracking-wide text-blue-700 uppercase dark:text-blue-300"
                        >
                            {{
                                t(
                                    'tools.image.home.editor_spotlight.eyebrow',
                                    'Featured',
                                )
                            }}
                        </p>
                        <h2
                            id="editor-spotlight-heading"
                            class="text-lg font-semibold tracking-tight text-foreground sm:text-xl"
                        >
                            {{
                                t(
                                    'tools.image.home.editor_spotlight.title',
                                    'Full image editor',
                                )
                            }}
                        </h2>
                        <p
                            class="max-w-2xl text-sm leading-relaxed text-pretty text-muted-foreground"
                        >
                            {{
                                t(
                                    'tools.image.home.editor_spotlight.description',
                                    'Crop, fine-tune, add filters, text, shapes, and watermarks—then save to your device.',
                                )
                            }}
                        </p>
                        <ul class="space-y-1.5" role="list">
                            <li
                                v-for="(
                                    feature, index
                                ) in editorSpotlightFeatures"
                                :key="index"
                                class="flex gap-2 text-sm text-muted-foreground"
                            >
                                <Check
                                    class="mt-0.5 size-3.5 shrink-0 text-blue-600 dark:text-blue-400"
                                    aria-hidden="true"
                                />
                                <span>{{ feature }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <Button
                    class="shrink-0 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                    as-child
                >
                    <Link :href="editorHref" prefetch>
                        {{
                            t(
                                'tools.image.home.editor_spotlight.cta',
                                'Open editor',
                            )
                        }}
                        <ArrowRight class="size-4" aria-hidden="true" />
                    </Link>
                </Button>
            </div>
        </section>

        <p
            class="max-w-3xl text-xs leading-relaxed text-pretty text-muted-foreground"
        >
            {{
                t(
                    'tools.data_retention_notice',
                    'We do not keep your uploads after a tool run finishes: files are stored only temporarily to perform the operation, then deleted from the server (generated downloads are removed after they are sent, where applicable).',
                )
            }}
        </p>

        <div class="space-y-8">
            <h2
                class="text-sm font-medium tracking-wider text-muted-foreground uppercase"
            >
                {{ t('tools.image.home.all_tools_heading', 'All tools') }}
            </h2>

            <div
                v-for="section in sections"
                :key="section.headingKey"
                class="space-y-4"
            >
                <h3
                    class="text-sm font-medium tracking-wider text-muted-foreground uppercase"
                >
                    {{ t(section.headingKey, section.headingFallback) }}
                </h3>
                <div
                    :class="
                        section.featured
                            ? 'grid max-w-md gap-3'
                            : 'grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3 2xl:grid-cols-4'
                    "
                >
                    <Link
                        v-for="tool in section.tools"
                        :key="tool.key + tool.href"
                        :href="tool.href"
                        prefetch
                        class="group touch-manipulation ring-offset-background outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        <Card
                            :class="
                                section.featured
                                    ? 'h-full border-blue-200/80 bg-blue-50/30 transition-colors hover:border-blue-300 hover:bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-950/20 dark:hover:border-blue-800 dark:hover:bg-blue-950/40'
                                    : 'h-full border-sidebar-border/70 transition-colors hover:border-emerald-200/80 hover:bg-accent/40 dark:border-sidebar-border dark:hover:border-emerald-800/50'
                            "
                        >
                            <CardHeader class="gap-3">
                                <div
                                    class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-muted transition-colors group-hover:brightness-95"
                                    :class="
                                        section.featured
                                            ? 'bg-blue-100 dark:bg-blue-950/60'
                                            : ''
                                    "
                                >
                                    <component
                                        :is="tool.icon"
                                        :class="['size-5', tool.iconClass]"
                                    />
                                </div>
                                <div class="space-y-1.5">
                                    <CardTitle
                                        class="text-base leading-snug group-hover:text-foreground"
                                    >
                                        {{
                                            t(
                                                `tools.image.${tool.key}.title`,
                                                tool.title,
                                            )
                                        }}
                                    </CardTitle>
                                    <CardDescription
                                        class="line-clamp-3 text-xs leading-relaxed sm:text-sm"
                                    >
                                        {{
                                            t(
                                                `tools.image.${tool.key}.description`,
                                                tool.description,
                                            )
                                        }}
                                    </CardDescription>
                                </div>
                            </CardHeader>
                        </Card>
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>
